/*
 * First-party, cookieless analytics.
 *
 * Events go to POST {REACT_APP_API_URL}/api/events on our own function app.
 * No cookies, no third parties, and no personal data: nothing typed into forms
 * is ever sent, and query strings are dropped from paths (they can carry emails);
 * only UTM campaign tags are kept, separately.
 *
 * ── Contract for the /api/events endpoint ───────────────────────────────────
 * Body is JSON sent with Content-Type text/plain (so sendBeacon works without a
 * CORS preflight) — parse the raw body as JSON. One event per request:
 *   {
 *     "type":      "pageview" | "event",
 *     "name":      string,          // "pageview" or e.g. "cta_click", "lead_form_success"
 *     "path":      string,          // "/retro-booth" — no query string
 *     "title":     string,          // document title
 *     "referrer":  string | null,   // external referrer host, first pageview of a session only
 *     "utm":       { source?, medium?, campaign?, term?, content? } | null,
 *     "props":     object,          // small, flat, event-specific (see EVENTS below)
 *     "sessionId": string,          // random per browser tab; never persisted beyond the tab
 *     "device":    "mobile" | "tablet" | "desktop",
 *     "ts":        string           // client ISO timestamp (prefer server time for reporting)
 *   }
 * Respond 202/204; the site ignores the response. Reject bodies over ~4KB.
 *
 * EVENTS (name → props):
 *   pageview                 —
 *   cta_click                { label, location }
 *   outbound_click           { url, label }
 *   email_click              { label }
 *   phone_click              { label }             // untagged tel: links (footer, contact page)
 *   file_download            { file }
 *   lead_form_start          —
 *   lead_form_invalid        { fields }            // client-side check failed
 *   lead_form_success        { eventType, packageName, daysUntilEvent }
 *   lead_form_rejected       { count }             // server returned 400
 *   lead_form_error          —                     // network / unexpected response
 *   blog_read_depth          { slug, percent }     // 25 / 50 / 75 / 100
 *   gallery_open             { gallery }
 *   web_vital                { metric, value }    // ms; CLS is x1000 (0.1 -> 100)
 * ─────────────────────────────────────────────────────────────────────────────
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7071';
export const EVENTS_URL = `${API_BASE}/api/events`;

const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'];

function randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function deviceClass(width) {
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

// Honour browser-level opt-outs.
function optedOut(nav) {
  return Boolean(nav && (nav.globalPrivacyControl === true || nav.doNotTrack === '1'));
}

/**
 * Factory so the tracker can be tested with a fake environment. The app uses
 * the default instance exported at the bottom of this file.
 */
export function createTracker({
  endpoint = EVENTS_URL,
  enabled = true,
  win = typeof window !== 'undefined' ? window : undefined,
  transport,
  log,
} = {}) {
  const nav = win && win.navigator;
  const active = enabled && !!win && !optedOut(nav) && !(nav && nav.webdriver);

  // Session id lives for the tab only (sessionStorage), never across visits.
  let sessionId;
  try {
    sessionId = win.sessionStorage.getItem('dd_sid');
    if (!sessionId) {
      sessionId = randomId();
      win.sessionStorage.setItem('dd_sid', sessionId);
    }
  } catch {
    sessionId = randomId(); // storage blocked: fall back to this page load
  }

  // Campaign tags and the external referrer belong to the landing page only.
  let landing = null;
  if (win) {
    const params = new URLSearchParams(win.location.search);
    const utm = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(`utm_${k}`);
      if (v) utm[k] = v.slice(0, 100);
    });
    let referrer = null;
    try {
      const ref = win.document.referrer && new URL(win.document.referrer);
      if (ref && ref.host !== win.location.host) referrer = ref.host;
    } catch {
      referrer = null;
    }
    landing = { utm: Object.keys(utm).length ? utm : null, referrer };
  }

  const send =
    transport ||
    ((body) => {
      try {
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        if (nav && nav.sendBeacon && nav.sendBeacon(endpoint, blob)) return;
        win.fetch(endpoint, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } })
          .catch(() => {}); // analytics must never surface errors to visitors
      } catch {
        // ignore
      }
    });

  function emit(type, name, props = {}) {
    if (!win) return;
    const isFirstPageview = type === 'pageview' && landing;
    const payload = {
      type,
      name,
      path: win.location.pathname,
      title: win.document.title,
      referrer: isFirstPageview ? landing.referrer : null,
      utm: isFirstPageview ? landing.utm : null,
      props,
      sessionId,
      device: deviceClass(win.innerWidth || 1024),
      ts: new Date().toISOString(),
    };
    if (isFirstPageview) landing = null; // attribute the landing data once
    if (log) log(payload);
    if (active) send(JSON.stringify(payload));
  }

  return {
    sessionId,
    active,
    pageview: () => emit('pageview', 'pageview'),
    track: (name, props) => emit('event', name, props),
  };
}

// Send only from real production builds; in development, log to the console instead
// so local testing never pollutes the numbers.
const isProd = process.env.NODE_ENV === 'production';
const tracker = createTracker({
  enabled: isProd,
  // eslint-disable-next-line no-console
  log: !isProd && process.env.NODE_ENV !== 'test' ? (p) => console.debug('[analytics]', p.name, p) : undefined,
});

export const pageview = tracker.pageview;
export const track = tracker.track;
export const analyticsSessionId = tracker.sessionId;
